import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import "./EditMatch.css";

export default function AdminEditMatches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");

  // TODAY | TOMORROW | ALL
  const [dateFilter, setDateFilter] = useState("TODAY");

  useEffect(() => {
    fetchMatches();
  }, []);

  async function fetchMatches() {
    setLoading(true);

    try {
      const snap = await getDocs(collection(db, "matches"));

      const list = snap.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      list.sort((a, b) => {
        const dateA = `${a.date || ""} ${a.time || ""}`;
        const dateB = `${b.date || ""} ${b.time || ""}`;

        return dateA.localeCompare(dateB);
      });

      setMatches(list);
    } catch (error) {
      console.error("FETCH MATCHES ERROR:", error);
      showMessage("❌ Imeshindikana kupakia matches.");
    } finally {
      setLoading(false);
    }
  }

  function showMessage(text) {
    setMessage(text);

    setTimeout(() => {
      setMessage("");
    }, 3500);
  }

  /*
   * ----------------------------------------
   * DATE HELPERS
   * ----------------------------------------
   */

  function getLocalDateString(offsetDays = 0) {
    const date = new Date();

    date.setDate(date.getDate() + offsetDays);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  const today = getLocalDateString(0);
  const tomorrow = getLocalDateString(1);

  /*
   * ----------------------------------------
   * UPDATE MATCH
   * ----------------------------------------
   */

  function updateMatch(id, field, value) {
    setMatches((prev) =>
      prev.map((match) =>
        match.id === id
          ? {
              ...match,
              [field]: value,
            }
          : match
      )
    );
  }

  /*
   * ----------------------------------------
   * UPDATE ODDS
   * ----------------------------------------
   */

  function updateOdds(id, field, value) {
    setMatches((prev) =>
      prev.map((match) => {
        if (match.id !== id) return match;

        return {
          ...match,
          odds: {
            ...(match.odds || {}),
            [field]: value,
          },
        };
      })
    );
  }

  /*
   * ----------------------------------------
   * UPDATE CORRECT SCORE
   * ----------------------------------------
   */

  function updateCorrectScore(id, index, field, value) {
    setMatches((prev) =>
      prev.map((match) => {
        if (match.id !== id) return match;

        const scores = Array.isArray(match.correctScores)
          ? [...match.correctScores]
          : [];

        scores[index] = {
          ...(scores[index] || {}),
          [field]: value,
        };

        return {
          ...match,
          correctScores: scores,
        };
      })
    );
  }

  /*
   * ----------------------------------------
   * ADD CORRECT SCORE
   * ----------------------------------------
   */

  function addCorrectScore(id) {
    setMatches((prev) =>
      prev.map((match) => {
        if (match.id !== id) return match;

        const scores = Array.isArray(match.correctScores)
          ? [...match.correctScores]
          : [];

        scores.push({
          score: "",
          odds: "",
        });

        return {
          ...match,
          correctScores: scores,
        };
      })
    );
  }

  /*
   * ----------------------------------------
   * REMOVE CORRECT SCORE
   * ----------------------------------------
   */

  function removeCorrectScore(id, index) {
    setMatches((prev) =>
      prev.map((match) => {
        if (match.id !== id) return match;

        const scores = Array.isArray(match.correctScores)
          ? [...match.correctScores]
          : [];

        scores.splice(index, 1);

        return {
          ...match,
          correctScores: scores,
        };
      })
    );
  }

  /*
   * ----------------------------------------
   * NUMBER NORMALIZER
   * ----------------------------------------
   */

  function normalizeNumber(value) {
    if (
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return null;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : null;
  }

  /*
   * ----------------------------------------
   * FINAL SCORE VALIDATION
   * Example:
   * 1-2
   * 0-0
   * 10-2
   * ----------------------------------------
   */

  function validateFinalScore(score) {
    if (!score || !String(score).trim()) {
      return true;
    }

    return /^\d+\s*-\s*\d+$/.test(String(score).trim());
  }

  /*
   * ----------------------------------------
   * SAVE MATCH
   * ----------------------------------------
   */

  async function saveMatch(match) {
    if (!match.A?.trim() || !match.B?.trim()) {
      showMessage("⚠️ Team A na Team B lazima zijazwe.");
      return;
    }

    if (!match.date) {
      showMessage("⚠️ Date lazima ijazwe.");
      return;
    }

    if (!match.time) {
      showMessage("⚠️ Time lazima ijazwe.");
      return;
    }

    if (!validateFinalScore(match.finalScore)) {
      showMessage("⚠️ Final Score iwe mfano 1-2.");
      return;
    }

    setSavingId(match.id);

    try {
      const correctScores = (match.correctScores || [])
        .filter(
          (item) =>
            item &&
            (item.score || item.odds !== "")
        )
        .map((item) => ({
          score: String(item.score || "").trim(),
          odds: normalizeNumber(item.odds),
        }));

      const payload = {
        A: String(match.A || "").trim(),
        B: String(match.B || "").trim(),

        country: String(match.country || "").trim(),

        league: String(match.league || "").trim(),

        date: String(match.date || "").trim(),

        time: String(match.time || "").trim(),

        timezone: String(
          match.timezone || "Africa/Dar_es_Salaam"
        ).trim(),

        logoA: String(match.logoA || "").trim(),

        logoB: String(match.logoB || "").trim(),

        leagueLogo: String(match.leagueLogo || "").trim(),

        odds: {
          home: normalizeNumber(match.odds?.home),
          draw: normalizeNumber(match.odds?.draw),
          away: normalizeNumber(match.odds?.away),
        },

        correctScores,

        sofaId: normalizeNumber(match.sofaId),

        source: String(
          match.source || "manual"
        ).trim(),

        finalScore: String(
          match.finalScore || ""
        ).trim(),

        updatedAt: serverTimestamp(),
      };

      await updateDoc(
        doc(db, "matches", match.id),
        payload
      );

      showMessage(
        `✅ ${match.A} vs ${match.B} imehifadhiwa.`
      );

      await fetchMatches();
    } catch (error) {
      console.error("SAVE MATCH ERROR:", error);

      showMessage(
        "❌ Kuna tatizo wakati wa kuhifadhi."
      );
    } finally {
      setSavingId(null);
    }
  }

  /*
   * ----------------------------------------
   * DELETE MATCH
   * ----------------------------------------
   */

  async function deleteMatch(match) {
    const confirmed = window.confirm(
      `Una uhakika unataka kufuta ${match.A} vs ${match.B}?`
    );

    if (!confirmed) return;

    try {
      await deleteDoc(
        doc(db, "matches", match.id)
      );

      setMatches((prev) =>
        prev.filter(
          (item) => item.id !== match.id
        )
      );

      showMessage("🗑️ Match imefutwa.");
    } catch (error) {
      console.error("DELETE MATCH ERROR:", error);

      showMessage(
        "❌ Imeshindikana kufuta match."
      );
    }
  }

  /*
   * ----------------------------------------
   * DATE FILTER
   *
   * TODAY
   * TOMORROW
   * ALL
   * ----------------------------------------
   */

  const dateFilteredMatches = useMemo(() => {
    if (dateFilter === "ALL") {
      return matches;
    }

    if (dateFilter === "TODAY") {
      return matches.filter(
        (match) => match.date === today
      );
    }

    if (dateFilter === "TOMORROW") {
      return matches.filter(
        (match) => match.date === tomorrow
      );
    }

    return matches;
  }, [
    matches,
    dateFilter,
    today,
    tomorrow,
  ]);

  /*
   * ----------------------------------------
   * SEARCH
   *
   * Search in selected date filter
   * ----------------------------------------
   */

  const filteredMatches = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return dateFilteredMatches;
    }

    return dateFilteredMatches.filter(
      (match) => {
        const text = `
          ${match.A || ""}
          ${match.B || ""}
          ${match.country || ""}
          ${match.league || ""}
          ${match.sofaId || ""}
          ${match.finalScore || ""}
          ${match.date || ""}
          ${match.time || ""}
          ${match.source || ""}
          ${match.timezone || ""}
        `.toLowerCase();

        return text.includes(query);
      }
    );
  }, [
    dateFilteredMatches,
    search,
  ]);

  /*
   * ----------------------------------------
   * COUNTS
   * ----------------------------------------
   */

  const todayCount = matches.filter(
    (match) => match.date === today
  ).length;

  const tomorrowCount = matches.filter(
    (match) => match.date === tomorrow
  ).length;

  const allCount = matches.length;

  return (
    <div className="admin-edit-matches">

      {/* =========================================
          HEADER
      ========================================= */}

      <div className="admin-edit-header">
        <div>
          <div className="admin-edit-kicker">
            MATCH CONTROL PANEL
          </div>

          <h1>EDIT MATCHES</h1>

          <p>
            Simamia na hariri taarifa zote za
            matches kutoka Firestore.
          </p>
        </div>

        <div className="match-counter">
          <span>
            {filteredMatches.length}
          </span>

          <small>
            MATCHES
          </small>
        </div>
      </div>

      {/* =========================================
          DATE FILTER TABS
      ========================================= */}

      <div className="match-date-tabs">

        <button
          type="button"
          className={
            dateFilter === "TODAY"
              ? "date-tab active"
              : "date-tab"
          }
          onClick={() => {
            setDateFilter("TODAY");
            setSearch("");
          }}
        >
          <span className="date-tab-icon">
            ◉
          </span>

          <span className="date-tab-content">
            <strong>
              TODAY
            </strong>

            <small>
              {today}
            </small>
          </span>

          <span className="date-tab-count">
            {todayCount}
          </span>
        </button>

        <button
          type="button"
          className={
            dateFilter === "TOMORROW"
              ? "date-tab active"
              : "date-tab"
          }
          onClick={() => {
            setDateFilter("TOMORROW");
            setSearch("");
          }}
        >
          <span className="date-tab-icon">
            ◷
          </span>

          <span className="date-tab-content">
            <strong>
              TOMORROW
            </strong>

            <small>
              {tomorrow}
            </small>
          </span>

          <span className="date-tab-count">
            {tomorrowCount}
          </span>
        </button>

        <button
          type="button"
          className={
            dateFilter === "ALL"
              ? "date-tab active"
              : "date-tab"
          }
          onClick={() => {
            setDateFilter("ALL");
            setSearch("");
          }}
        >
          <span className="date-tab-icon">
            ▦
          </span>

          <span className="date-tab-content">
            <strong>
              ALL
            </strong>

            <small>
              All matches
            </small>
          </span>

          <span className="date-tab-count">
            {allCount}
          </span>
        </button>

      </div>

      {/* =========================================
          SEARCH TOOLBAR
      ========================================= */}

      <div className="admin-edit-toolbar">

        <div className="search-box">
          <span>
            ⌕
          </span>

          <input
            type="text"
            placeholder={
              dateFilter === "TODAY"
                ? "Search today's matches..."
                : dateFilter === "TOMORROW"
                ? "Search tomorrow's matches..."
                : "Search team, league, country, Sofa ID..."
            }
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />

          {search && (
            <button
              type="button"
              className="clear-search-btn"
              onClick={() =>
                setSearch("")
              }
            >
              ×
            </button>
          )}
        </div>

        <button
          className="refresh-btn"
          onClick={fetchMatches}
          disabled={loading}
        >
          {loading
            ? "Loading..."
            : "↻ Refresh"}
        </button>

      </div>

      {/* =========================================
          CURRENT FILTER INFO
      ========================================= */}

      <div className="active-filter-info">

        <div>
          <span>
            SHOWING
          </span>

          <strong>
            {dateFilter === "TODAY"
              ? "TODAY"
              : dateFilter === "TOMORROW"
              ? "TOMORROW"
              : "ALL MATCHES"}
          </strong>
        </div>

        <div>
          <span>
            RESULTS
          </span>

          <strong>
            {filteredMatches.length}
          </strong>
        </div>

        {search && (
          <div>
            <span>
              SEARCH
            </span>

            <strong>
              "{search}"
            </strong>
          </div>
        )}

      </div>

      {/* =========================================
          MESSAGE
      ========================================= */}

      {message && (
        <div className="admin-edit-message">
          {message}
        </div>
      )}

      {/* =========================================
          LOADING
      ========================================= */}

      {loading ? (
        <div className="admin-edit-loading">

          <div className="digital-loader"></div>

          <span>
            Loading matches...
          </span>

        </div>
      ) : filteredMatches.length === 0 ? (

        /* =======================================
           EMPTY
        ======================================= */

        <div className="admin-edit-empty">

          <div>
            ⌁
          </div>

          <h3>
            No matches found
          </h3>

          <p>
            {search
              ? `Hakuna match inayolingana na "${search}".`
              : dateFilter === "TODAY"
              ? "Hakuna matches za leo."
              : dateFilter === "TOMORROW"
              ? "Hakuna matches za kesho."
              : "Hakuna matches kwenye collection."}
          </p>

          {dateFilter !== "ALL" && (
            <button
              type="button"
              onClick={() => {
                setDateFilter("ALL");
                setSearch("");
              }}
            >
              VIEW ALL MATCHES
            </button>
          )}

        </div>

      ) : (

        /* =======================================
           MATCH LIST
        ======================================= */

        <div className="matches-editor-list">

          {filteredMatches.map(
            (match, matchIndex) => {

              const scores =
                Array.isArray(
                  match.correctScores
                )
                  ? match.correctScores
                  : [];

              const isSaving =
                savingId === match.id;

              return (
                <section
                  className="match-editor-card"
                  key={match.id}
                >

                  {/* =================================
                      MATCH TOP
                  ================================= */}

                  <div className="match-editor-top">

                    <div className="match-number">
                      #
                      {String(
                        matchIndex + 1
                      ).padStart(2, "0")}
                    </div>

                    <div className="match-title">

                      <strong>
                        {match.A ||
                          "Team A"}{" "}

                        <span>
                          VS
                        </span>{" "}

                        {match.B ||
                          "Team B"}
                      </strong>

                      <small>
                        ID: {match.id}
                      </small>

                    </div>

                    <div className="match-editor-actions">

                      <button
                        className="delete-match-btn"
                        onClick={() =>
                          deleteMatch(match)
                        }
                      >
                        Delete
                      </button>

                      <button
                        className="save-match-btn"
                        onClick={() =>
                          saveMatch(match)
                        }
                        disabled={isSaving}
                      >
                        {isSaving
                          ? "Saving..."
                          : "Save Match"}
                      </button>

                    </div>

                  </div>

                  {/* =================================
                      MATCH INFORMATION
                  ================================= */}

                  <div className="editor-section">

                    <div className="section-title">

                      <span>
                        01
                      </span>

                      MATCH INFORMATION

                    </div>

                    <div className="editor-grid">

                      <label>
                        <span>
                          TEAM A
                        </span>

                        <input
                          type="text"
                          value={
                            match.A || ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "A",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          TEAM B
                        </span>

                        <input
                          type="text"
                          value={
                            match.B || ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "B",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          COUNTRY
                        </span>

                        <input
                          type="text"
                          value={
                            match.country ||
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "country",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          LEAGUE
                        </span>

                        <input
                          type="text"
                          value={
                            match.league ||
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "league",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          DATE
                        </span>

                        <input
                          type="date"
                          value={
                            match.date || ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "date",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          TIME
                        </span>

                        <input
                          type="time"
                          value={
                            match.time || ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "time",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          TIMEZONE
                        </span>

                        <input
                          type="text"
                          value={
                            match.timezone ||
                            "Africa/Dar_es_Salaam"
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "timezone",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          SOFA ID
                        </span>

                        <input
                          type="number"
                          value={
                            match.sofaId ??
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "sofaId",
                              e.target.value
                            )
                          }
                        />
                      </label>

                      <label>
                        <span>
                          SOURCE
                        </span>

                        <input
                          type="text"
                          value={
                            match.source ||
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "source",
                              e.target.value
                            )
                          }
                        />
                      </label>

                    </div>
                  </div>

                  {/* =================================
                      LOGOS
                  ================================= */}

                  <div className="editor-section">

                    <div className="section-title">

                      <span>
                        02
                      </span>

                      MATCH LOGOS

                    </div>

                    <div className="editor-grid">

                      <label className="full-width">

                        <span>
                          TEAM A LOGO URL
                        </span>

                        <input
                          type="text"
                          value={
                            match.logoA ||
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "logoA",
                              e.target.value
                            )
                          }
                          placeholder="https://..."
                        />

                      </label>

                      <label className="full-width">

                        <span>
                          TEAM B LOGO URL
                        </span>

                        <input
                          type="text"
                          value={
                            match.logoB ||
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "logoB",
                              e.target.value
                            )
                          }
                          placeholder="https://..."
                        />

                      </label>

                      <label className="full-width">

                        <span>
                          LEAGUE LOGO URL
                        </span>

                        <input
                          type="text"
                          value={
                            match.leagueLogo ||
                            ""
                          }
                          onChange={(e) =>
                            updateMatch(
                              match.id,
                              "leagueLogo",
                              e.target.value
                            )
                          }
                          placeholder="https://..."
                        />

                      </label>

                    </div>
                  </div>

                  {/* =================================
                      1X2 ODDS
                  ================================= */}

                  <div className="editor-section">

                    <div className="section-title">

                      <span>
                        03
                      </span>

                      1X2 ODDS

                    </div>

                    <div className="odds-editor">

                      <label className="odds-field home">

                        <span>
                          HOME
                        </span>

                        <input
                          type="number"
                          step="0.01"
                          value={
                            match.odds
                              ?.home ?? ""
                          }
                          onChange={(e) =>
                            updateOdds(
                              match.id,
                              "home",
                              e.target.value
                            )
                          }
                        />

                      </label>

                      <label className="odds-field draw">

                        <span>
                          DRAW
                        </span>

                        <input
                          type="number"
                          step="0.01"
                          value={
                            match.odds
                              ?.draw ?? ""
                          }
                          onChange={(e) =>
                            updateOdds(
                              match.id,
                              "draw",
                              e.target.value
                            )
                          }
                        />

                      </label>

                      <label className="odds-field away">

                        <span>
                          AWAY
                        </span>

                        <input
                          type="number"
                          step="0.01"
                          value={
                            match.odds
                              ?.away ?? ""
                          }
                          onChange={(e) =>
                            updateOdds(
                              match.id,
                              "away",
                              e.target.value
                            )
                          }
                        />

                      </label>

                    </div>
                  </div>

                  {/* =================================
                      FINAL SCORE
                  ================================= */}

                  <div className="editor-section">

                    <div className="section-title">

                      <span>
                        04
                      </span>

                      FINAL RESULT

                    </div>

                    <div className="final-score-panel">

                      <div className="final-score-info">

                        <strong>
                          FINAL SCORE
                        </strong>

                        <span>
                          Weka matokeo ya mwisho
                          mfano:
                          <b>
                            {" "}1-2
                          </b>
                        </span>

                      </div>

                      <input
                        className="final-score-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        placeholder="1-2"
                        value={
                          match.finalScore ||
                          ""
                        }
                        onChange={(e) =>
                          updateMatch(
                            match.id,
                            "finalScore",
                            e.target.value
                          )
                        }
                      />

                    </div>
                  </div>

                  {/* =================================
                      CORRECT SCORES
                  ================================= */}

                  <div className="editor-section">

                    <div className="section-title">

                      <span>
                        05
                      </span>

                      CORRECT SCORES

                    </div>

                    <div className="correct-score-header">

                      <span>
                        SCORE
                      </span>

                      <span>
                        ODDS
                      </span>

                      <span>
                        ACTION
                      </span>

                    </div>

                    <div className="correct-score-list">

                      {scores.map(
                        (item, index) => (

                          <div
                            className="correct-score-row"
                            key={`${match.id}-${index}`}
                          >

                            <div className="score-index">
                              {String(
                                index + 1
                              ).padStart(
                                2,
                                "0"
                              )}
                            </div>

                            <input
                              type="text"
                              placeholder="1-2"
                              value={
                                item?.score ||
                                ""
                              }
                              onChange={(e) =>
                                updateCorrectScore(
                                  match.id,
                                  index,
                                  "score",
                                  e.target.value
                                )
                              }
                            />

                            <input
                              type="number"
                              step="0.01"
                              placeholder="8.50"
                              value={
                                item?.odds ??
                                ""
                              }
                              onChange={(e) =>
                                updateCorrectScore(
                                  match.id,
                                  index,
                                  "odds",
                                  e.target.value
                                )
                              }
                            />

                            <button
                              className="remove-score-btn"
                              onClick={() =>
                                removeCorrectScore(
                                  match.id,
                                  index
                                )
                              }
                            >
                              ×
                            </button>

                          </div>

                        )
                      )}

                      <button
                        className="add-score-btn"
                        onClick={() =>
                          addCorrectScore(
                            match.id
                          )
                        }
                      >
                        + ADD CORRECT SCORE
                      </button>

                    </div>
                  </div>

                  {/* =================================
                      FOOTER
                  ================================= */}

                  <div className="editor-footer">

                    <span>

                      LAST UPDATE:{" "}

                      {match.updatedAt?.toDate
                        ? match.updatedAt
                            .toDate()
                            .toLocaleString()
                        : "—"}

                    </span>

                    <span>

                      CREATED BY:{" "}
                      {match.createdBy ||
                        "—"}

                    </span>

                  </div>

                </section>
              );
            }
          )}

        </div>
      )}

    </div>
  );
}